/**
 * VizPivotBuilder - Excel-style pivot chart builder.
 *
 * Users click fields from the Available Fields panel to assign them to
 * X Axis, Series, or Measure zones. The component generates Plotly JS code
 * via vizPivotGenerator and calls onChange() with the result.
 *
 * Props:
 *   viewSchemas      { [viewId]: viewObject }  - Cached view objects
 *   selectedViewIds  {string[]}               - Which view IDs are active
 *   code             {string}                 - Current js_content (to parse existing config)
 *   onChange         {fn(codeString)}         - Called when config changes
 *   onPreview        {fn}                     - Called to trigger preview
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { generatePivotCode, parsePivotConfig, isNumeric, isDate } from '../../lib/vizPivotGenerator';
import VizFormatModal from './VizFormatModal';

// ─── Constants ──────────────────────────────────────────────────────────────

const AGG_OPTIONS_NUMERIC  = ['sum', 'avg', 'min', 'max', 'count', 'none'];
const AGG_OPTIONS_NONNUMERIC = ['count'];

const CHART_TYPE_OPTIONS_DEFAULT = ['bar', 'line', 'area', 'area_pct'];
const CHART_TYPE_LABELS = {
  bar: 'Bar', line: 'Line', area: 'Area', area_pct: 'Area%', pie: 'Pie', scatter: 'Scatter',
};

const DATE_GROUP_OPTIONS = [
  { value: 'day',     label: 'Day' },
  { value: 'week',    label: 'Week' },
  { value: 'month',   label: 'Month' },
  { value: 'quarter', label: 'Quarter' },
  { value: 'year',    label: 'Year' },
];
const SORT_OPTIONS = [
  { value: 'asc',        label: 'A→Z' },
  { value: 'desc',       label: 'Z→A' },
  { value: 'value_asc',  label: 'Value ↑' },
  { value: 'value_desc', label: 'Value ↓' },
];

const EMPTY_CONFIG = { xAxis: null, series: null, measures: [], formatting: {} };

// ─── Helpers ─────────────────────────────────────────────────────────────────

function fieldKey(f) {
  return `${f.slug}:${f.field}`;
}

function defaultAgg(type) {
  return isNumeric(type) ? 'sum' : 'count';
}

function typeColor(type) {
  if (!type) return 'bg-gray-100 text-gray-500';
  if (isNumeric(type)) return 'bg-green-100 text-green-700';
  if (isDate(type)) return 'bg-blue-100 text-blue-700';
  return 'bg-gray-100 text-gray-500';
}

function isTopicField(fieldName) {
  return typeof fieldName === 'string' && fieldName.startsWith('bnstopic_');
}

function chartTypeOptions(measureIndex, hasPie, hasXAxis, agg) {
  if (agg === 'none') return ['scatter'];
  const opts = [...CHART_TYPE_OPTIONS_DEFAULT];
  // Pie only when no X axis and first measure
  if (!hasXAxis && measureIndex === 0) opts.push('pie');
  return opts;
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function FieldCapsule({ field, onClick }) {
  const isTopic = isTopicField(field.field);
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1 px-2 py-1 rounded-md border hover:shadow-sm transition text-sm cursor-pointer ${isTopic ? 'bg-violet-50 border-violet-200 hover:border-violet-400' : 'bg-white border-gray-200 hover:border-primary-400'}`}
      title={`${field.field} : ${field.type}\n(click to assign)`}
    >
      {isTopic && <span className="text-violet-400 text-xs">◆</span>}
      <span className={`font-medium ${isTopic ? 'text-violet-700' : 'text-gray-800'}`}>{field.field}</span>
      <span className={`text-xs px-1 rounded ${isTopic ? 'bg-violet-100 text-violet-600' : typeColor(field.type)}`}>
        {isTopic ? 'TOPIC' : field.type?.toUpperCase().slice(0, 7)}
      </span>
    </button>
  );
}

function AssignedCapsule({ label, onRemove, children }) {
  return (
    <div className="flex items-center gap-1.5 px-2 py-1.5 rounded-md bg-primary-50 border border-primary-200">
      <div className="flex flex-col gap-1 flex-1 min-w-0">
        <span className="text-xs font-medium text-primary-700">{label}</span>
        {children}
      </div>
      <button
        onClick={onRemove}
        className="text-primary-400 hover:text-red-500 transition ml-1 text-base leading-none"
        title="Remove"
      >
        ×
      </button>
    </div>
  );
}

function ZoneLabel({ title, subtitle }) {
  return (
    <div className="mb-1.5">
      <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">{title}</span>
      {subtitle && <span className="text-xs text-gray-400 ml-1">({subtitle})</span>}
    </div>
  );
}

function Zone({ className = '', children }) {
  return (
    <div className={`rounded-lg border-2 border-dashed border-gray-200 bg-gray-50 p-3 min-h-[80px] ${className}`}>
      {children}
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────

export default function VizPivotBuilder({ viewSchemas = {}, selectedViewIds = [], code = '', onChange, onPreview }) {
  // Parse existing config on mount
  const [config, setConfig] = useState(() => {
    const parsed = parsePivotConfig(code);
    return parsed || EMPTY_CONFIG;
  });
  const [warning, setWarning]           = useState(null);
  const [showFormatModal, setShowFormatModal] = useState(false);
  const [activePopover, setActivePopover]     = useState(null); // field key or null

  const warningTimer = useRef(null);
  const onChangeRef  = useRef(onChange);
  onChangeRef.current = onChange;

  // Regenerate code whenever config changes
  useEffect(() => {
    const generated = generatePivotCode(config);
    onChangeRef.current?.(generated);
  }, [config]);

  // Close popover on outside click
  useEffect(() => {
    if (!activePopover) return;
    const handler = () => setActivePopover(null);
    document.addEventListener('click', handler, { once: true });
    return () => document.removeEventListener('click', handler);
  }, [activePopover]);

  const showWarn = useCallback((msg) => {
    setWarning(msg);
    clearTimeout(warningTimer.current);
    warningTimer.current = setTimeout(() => setWarning(null), 4000);
  }, []);

  // ── Derived: available fields ─────────────────────────────────────────────

  const assignedKeys = new Set([
    config.xAxis  ? fieldKey(config.xAxis)  : null,
    config.series ? fieldKey(config.series) : null,
    ...config.measures.map(m => fieldKey(m)),
  ].filter(Boolean));

  const allFields = selectedViewIds.flatMap(id => {
    const view = viewSchemas[id];
    if (!view?.output_schema?.columns) return [];
    return view.output_schema.columns.map(col => ({
      slug:     view.slug,
      field:    col.name,
      type:     col.type,
      viewName: view.name,
      key:      `${view.slug}:${col.name}`,
    }));
  });

  const availableFields = allFields.filter(f => !assignedKeys.has(f.key));

  // ── Validation helpers ────────────────────────────────────────────────────

  const hasPie  = config.measures.some(m => m.chartType === 'pie');
  const hasXAxis = !!config.xAxis;

  function canAssign(field, role) {
    switch (role) {
      case 'xAxis':
        if (config.xAxis) return 'X Axis already assigned';
        if (hasPie)       return 'Remove pie chart type before adding an X axis';
        return null;
      case 'series':
        if (config.series) return 'Series already assigned';
        return null;
      case 'measure': {
        if (config.measures.length >= 2)                          return 'Maximum 2 measures';
        if (config.measures.length === 1 && hasPie)               return 'Pie chart only supports 1 measure';
        if (config.measures.length === 1 && config.measures[0].agg === 'none') return 'Scatter chart only supports 1 measure';
        return null;
      }
      default: return null;
    }
  }

  // ── Assignment ────────────────────────────────────────────────────────────

  function assignField(field, role) {
    const err = canAssign(field, role);
    if (err) { showWarn(err); return; }

    setActivePopover(null);

    if (role === 'xAxis') {
      setConfig(c => ({
        ...c,
        xAxis: {
          slug:      field.slug,
          field:     field.field,
          type:      field.type,
          dateGroup: isDate(field.type) ? 'month' : null,
          sort:      'asc',
        },
      }));
    } else if (role === 'series') {
      setConfig(c => ({ ...c, series: { slug: field.slug, field: field.field, type: field.type } }));
    } else if (role === 'measure') {
      const firstChartType = config.measures[0]?.chartType || 'bar';
      setConfig(c => ({
        ...c,
        measures: [
          ...c.measures,
          {
            slug:      field.slug,
            field:     field.field,
            type:      field.type,
            agg:       defaultAgg(field.type),
            chartType: firstChartType,
          },
        ],
      }));
    }
  }

  // ── Removal ───────────────────────────────────────────────────────────────

  function removeXAxis() { setConfig(c => ({ ...c, xAxis: null })); }
  function removeSeries() { setConfig(c => ({ ...c, series: null })); }
  function removeMeasure(idx) {
    setConfig(c => ({ ...c, measures: c.measures.filter((_, i) => i !== idx) }));
  }

  // ── Measure updates ───────────────────────────────────────────────────────

  function updateMeasure(idx, key, value) {
    setConfig(c => {
      const measures = [...c.measures];
      measures[idx] = { ...measures[idx], [key]: value };

      // Scatter validations
      if (key === 'agg' && value === 'none') {
        if (!c.xAxis) { showWarn('Scatter chart requires an X axis'); }
        if (c.measures.length > 1) { showWarn('Scatter chart only supports 1 measure'); }
        measures[idx].chartType = 'scatter';
      }

      // Pie validations
      if (key === 'chartType' && value === 'pie') {
        if (c.xAxis) { showWarn('Remove the X axis to use pie chart'); }
        if (c.measures.length > 1) { showWarn('Switch to 1 measure to use pie chart'); }
      }

      return { ...c, measures };
    });
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-4">

      {/* Available Fields */}
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
          Available Fields
          {availableFields.length === 0 && allFields.length === 0 && (
            <span className="ml-2 font-normal normal-case text-gray-400">— select a data source view above</span>
          )}
        </div>
        {allFields.length > 0 && availableFields.length === 0 ? (
          <p className="text-xs text-gray-400 italic">All fields assigned</p>
        ) : (() => {
          const topicFields = availableFields.filter(f => isTopicField(f.field));
          const regularFields = availableFields.filter(f => !isTopicField(f.field));
          const renderCapsule = field => (
            <div key={field.key} className="relative">
              <FieldCapsule
                field={field}
                onClick={e => {
                  e.stopPropagation();
                  setActivePopover(prev => prev === field.key ? null : field.key);
                }}
              />
              {activePopover === field.key && (
                <AssignPopover
                  field={field}
                  onAssign={(role) => assignField(field, role)}
                  canXAxis={canAssign(field, 'xAxis')}
                  canSeries={canAssign(field, 'series')}
                  canMeasure={canAssign(field, 'measure')}
                />
              )}
            </div>
          );
          return (
            <>
              {topicFields.length > 0 && (
                <div className="mb-2">
                  <div className="text-xs text-violet-500 font-medium mb-1.5 flex items-center gap-1">
                    <span>◆</span> Topic columns — use as Series to group charts by topic
                  </div>
                  <div className="flex flex-wrap gap-2">{topicFields.map(renderCapsule)}</div>
                </div>
              )}
              {regularFields.length > 0 && (
                <div className="flex flex-wrap gap-2">{regularFields.map(renderCapsule)}</div>
              )}
            </>
          );
        })()}
      </div>

      {/* Zones */}
      <div className="grid grid-cols-3 gap-3">

        {/* X Axis */}
        <div>
          <ZoneLabel title="X Axis" subtitle="1 field" />
          <Zone>
            {config.xAxis ? (
              <AssignedCapsule label={config.xAxis.field} onRemove={removeXAxis}>
                {config.xAxis.dateGroup !== null ? (
                  <select
                    value={config.xAxis.dateGroup}
                    onChange={e => setConfig(c => ({ ...c, xAxis: { ...c.xAxis, dateGroup: e.target.value } }))}
                    className="text-xs border rounded px-1 py-0.5 bg-white focus:outline-none"
                    onClick={e => e.stopPropagation()}
                  >
                    {DATE_GROUP_OPTIONS.map(o => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                ) : (
                  <select
                    value={config.xAxis.sort || 'asc'}
                    onChange={e => setConfig(c => ({ ...c, xAxis: { ...c.xAxis, sort: e.target.value } }))}
                    className="text-xs border rounded px-1 py-0.5 bg-white focus:outline-none"
                    onClick={e => e.stopPropagation()}
                  >
                    {SORT_OPTIONS.map(o => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                )}
              </AssignedCapsule>
            ) : (
              <p className="text-xs text-gray-400 italic">Click a field → X Axis</p>
            )}
          </Zone>
        </div>

        {/* Series */}
        <div>
          <ZoneLabel title="Series" subtitle="1 field" />
          <Zone>
            {config.series ? (
              <AssignedCapsule label={config.series.field} onRemove={removeSeries} />
            ) : (
              <p className="text-xs text-gray-400 italic">Click a field → Series</p>
            )}
          </Zone>
        </div>

        {/* Measures */}
        <div>
          <ZoneLabel title="Measures" subtitle="max 2" />
          <Zone>
            {config.measures.length === 0 ? (
              <p className="text-xs text-gray-400 italic">Click a field → Measure</p>
            ) : (
              <div className="space-y-2">
                {config.measures.map((m, idx) => {
                  const aggOpts = isNumeric(m.type) ? AGG_OPTIONS_NUMERIC : AGG_OPTIONS_NONNUMERIC;
                  const ctOpts  = chartTypeOptions(idx, hasPie, hasXAxis, m.agg);
                  const isLocked = m.agg === 'none'; // scatter: lock chart type

                  return (
                    <AssignedCapsule key={idx} label={m.field} onRemove={() => removeMeasure(idx)}>
                      <div className="flex gap-1 flex-wrap">
                        {/* Aggregation */}
                        <select
                          value={m.agg}
                          onChange={e => updateMeasure(idx, 'agg', e.target.value)}
                          disabled={!isNumeric(m.type)}
                          className="text-xs border rounded px-1 py-0.5 bg-white focus:outline-none disabled:opacity-50"
                          onClick={e => e.stopPropagation()}
                          title="Aggregation"
                        >
                          {aggOpts.map(a => (
                            <option key={a} value={a}>{a.charAt(0).toUpperCase() + a.slice(1)}</option>
                          ))}
                        </select>

                        {/* Chart type */}
                        <select
                          value={isLocked ? 'scatter' : m.chartType}
                          onChange={e => updateMeasure(idx, 'chartType', e.target.value)}
                          disabled={isLocked}
                          className="text-xs border rounded px-1 py-0.5 bg-white focus:outline-none disabled:opacity-50"
                          onClick={e => e.stopPropagation()}
                          title="Chart type"
                        >
                          {ctOpts.map(ct => (
                            <option key={ct} value={ct}>{CHART_TYPE_LABELS[ct]}</option>
                          ))}
                        </select>

                        {idx > 0 && (
                          <span className="text-xs text-gray-400 self-center">Y2</span>
                        )}
                      </div>
                    </AssignedCapsule>
                  );
                })}
              </div>
            )}
          </Zone>
        </div>
      </div>

      {/* Warning toast */}
      {warning && (
        <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-300 rounded-lg text-amber-800 text-sm">
          <span className="text-amber-500">⚠</span>
          {warning}
        </div>
      )}

      {/* Format button */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => setShowFormatModal(true)}
          className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition flex items-center gap-1"
        >
          <span>⚙</span> Format
        </button>
        {onPreview && (
          <button
            onClick={onPreview}
            className="px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
          >
            Preview
          </button>
        )}
      </div>

      {/* Format Modal */}
      <VizFormatModal
        open={showFormatModal}
        onClose={() => setShowFormatModal(false)}
        formatting={config.formatting}
        onChange={newFmt => setConfig(c => ({ ...c, formatting: newFmt }))}
        hasDualAxis={config.measures.length > 1}
        hasSeries={!!config.series}
      />
    </div>
  );
}

// ─── Assign Popover ──────────────────────────────────────────────────────────

function AssignPopover({ field, onAssign, canXAxis, canSeries, canMeasure }) {
  return (
    <div
      className="absolute z-30 top-full left-0 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 py-1 w-36"
      onClick={e => e.stopPropagation()}
    >
      <p className="px-3 py-1 text-xs font-semibold text-gray-400 uppercase tracking-wide">
        Assign as
      </p>
      {[
        { role: 'xAxis',   label: 'X Axis',  err: canXAxis },
        { role: 'series',  label: 'Series',  err: canSeries },
        { role: 'measure', label: 'Measure', err: canMeasure },
      ].map(({ role, label, err }) => (
        <button
          key={role}
          onClick={() => onAssign(role)}
          disabled={!!err}
          title={err || ''}
          className={`w-full text-left px-3 py-1.5 text-sm transition ${
            err ? 'text-gray-300 cursor-not-allowed' : 'text-gray-700 hover:bg-primary-50 hover:text-primary-700'
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  );
}
